function header() {
  return {
    mobileOpen: false,
    headerHeight: 0,
    announcementHeight: 0,
    isAtTop: true,
    shouldBeTransparent: false,
    menuIsOpen: false,
    isHeaderHovered: false,
    isPDP: false,
    isMobile: window.innerWidth < 768,

    // perf helpers
    heroEl: null,
    _nearViewportObserver: null,
    _rafId: null,
    _needsEval: false,

    // accessibility
    prefersReducedMotion: false,
    _prmMedia: null,
    _onPrmChange: null,

    init() {
      if (
        document.querySelector("#MainProduct-template--") ||
        window.location.pathname.includes("/products/") ||
        window.location.pathname === "/"
      ) {
        this.shouldBeTransparent = true;
      }
      if (document.querySelector("#MainProduct-template--") || window.location.pathname.includes("/products/")) {
        this.isPDP = true;
      }

      // Cache hero once (PDP hero preferred if present)
      this.heroEl = document.querySelector("#pdp-hero-section") || document.querySelector("#sliding-hero");

      this.isMobile = window.innerWidth < 768;

      // PRM setup
      this._prmMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.prefersReducedMotion = this._prmMedia.matches;
      this._onPrmChange = (e) => {
        this.prefersReducedMotion = e.matches;

        // Stop any running loops/observers
        this._stopRaf();
        this._nearViewportObserver?.disconnect();
        this._nearViewportObserver = null;

        if (this.prefersReducedMotion) {
          // Force solid header
          this.shouldBeTransparent = false;
        } else {
          // Re-enable PDP desktop 100vh behavior
          if (this.isPDP && !this.isMobile && this.heroEl) this._setupNearViewportObserver();
          this._evaluateHeaderOnce();
        }
      };
      if (this._prmMedia.addEventListener) {
        this._prmMedia.addEventListener("change", this._onPrmChange);
      } else if (this._prmMedia.addListener) {
        this._prmMedia.addListener(this._onPrmChange);
      }

      this.calculateHeights();

      // Start observers only for PDP desktop and when PRM is off
      if (this.heroEl && !this.prefersReducedMotion) {
        this._setupNearViewportObserver();
      }

      this._evaluateHeaderOnce(); // initial evaluation
    },

    // --- Core logic: rules in priority order ---
    _evaluateHeaderOnce() {
      const scrolled = window.scrollY;
      this.isAtTop = scrolled <= this.announcementHeight;

      // 1) Accessibility override: PRM => always solid
      if (this.prefersReducedMotion) {
        this.shouldBeTransparent = false;
        this.$dispatch("header-scroll-state", { isAtTop: this.isAtTop });
        return;
      }

      let keepTransparent = false;

      // 2) PDP on Mobile: initially transparent, solid as soon as user scrolls
      if (this.isPDP && this.isMobile) {
        keepTransparent = scrolled === 0;
      }
      // 3) General 100vh rule (PDP desktop or non-PDP hero pages)
      else if (this.heroEl) {
        const rect = this.heroEl.getBoundingClientRect();
        const threshold = window.innerHeight; // 100vh

        if (rect.bottom <= 0) {
          keepTransparent = false; // fully past
        } else {
          // More than 100vh left before it's scrolled past → keep transparent
          keepTransparent = rect.bottom > threshold; // (strict > for clean flip)
        }
      } else {
        // 4) No hero on the page → transparent only at absolute top, solid on any scroll
        // keepTransparent = scrolled === 0;
        keepTransparent = false;
      }

      // Respect menu/hover guards
      this.shouldBeTransparent = keepTransparent && !this.menuIsOpen && !this.isHeaderHovered;

      this.$dispatch("header-scroll-state", { isAtTop: this.isAtTop });
    },

    // rAF loop only while hero is near viewport (gated by IO)
    _tick() {
      if (!this._needsEval) return;
      this._evaluateHeaderOnce();
      this._rafId = requestAnimationFrame(() => this._tick());
    },

    _startRaf() {
      if (this._rafId == null) {
        this._needsEval = true;
        this._rafId = requestAnimationFrame(() => this._tick());
      }
    },

    _stopRaf() {
      this._needsEval = false;
      if (this._rafId != null) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
    },

    // IO that turns evaluation ON only when hero is near viewport (PDP desktop only)
    _setupNearViewportObserver() {
      if (!this.heroEl || this._nearViewportObserver) return;

      this._nearViewportObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            this._startRaf();
          } else {
            this._stopRaf();
            this._evaluateHeaderOnce(); // lock state when far away
          }
        },
        {
          root: null,
          rootMargin: "150% 0px 150% 0px", // extend top & bottom by ~1.5× viewport
          threshold: 0
        }
      );

      this._nearViewportObserver.observe(this.heroEl);
    },

    // Public scroll handler
    handleScroll() {
      // PDP mobile: solid on first scroll → evaluate every scroll
      // No hero: we also evaluate every scroll for instant flip at 1px
      if ((this.isPDP && this.isMobile) || !this.heroEl) {
        this._evaluateHeaderOnce();
      } else {
        // With a hero on non-PDP/PDP-desktop, IO+rAF keeps it fresh.
        // Still keep announcement "isAtTop" updated:
        const scrolled = window.scrollY;
        this.isAtTop = scrolled <= this.announcementHeight;
        this.$dispatch("header-scroll-state", { isAtTop: this.isAtTop });
      }
    },

    toggleMobile(isOpen) {
      this.mobileOpen = isOpen;
      this.$dispatch("toggle-mobile-header", isOpen);
      this._evaluateHeaderOnce();
    },

    toggleTransparent() {
      this.shouldBeTransparent = !this.shouldBeTransparent;
    },

    handleMenuStateChange(event) {
      this.menuIsOpen = event.detail.isOpen;
      this._evaluateHeaderOnce();
    },

    handleResize() {
      this.isMobile = window.innerWidth < 768;

      // PDP desktop <-> mobile switch should reconfigure observers
      this._nearViewportObserver?.disconnect();
      this._nearViewportObserver = null;
      this._stopRaf();

      if (this.isPDP && !this.isMobile && !this.prefersReducedMotion && this.heroEl) {
        this._setupNearViewportObserver();
      }

      this._evaluateHeaderOnce(); // 100vh threshold depends on viewport height
    },

    calculateHeights() {
      const headerElement = this.$refs.header;
      const announcementElement = this.$refs.announcementBar;

      if (!headerElement) return;

      const updateHeights = () => {
        this.headerHeight = headerElement.offsetHeight;
        this.announcementHeight = announcementElement ? announcementElement.offsetHeight : 0;

        document.documentElement.style.setProperty("--header-height", `${this.headerHeight}px`);
        document.documentElement.style.setProperty("--announcement-bar-height", `${this.announcementHeight}px`);
        document.documentElement.style.setProperty(
          "--total-header-height",
          `${this.headerHeight + this.announcementHeight}px`
        );

        this._evaluateHeaderOnce();
      };

      updateHeights();

      const resizeObserver = new ResizeObserver(updateHeights);
      resizeObserver.observe(headerElement);
      if (announcementElement) {
        resizeObserver.observe(announcementElement);
      }

      // Passive listeners
      const scrollHandler = () => this.handleScroll();
      const resizeHandler = () => this.handleResize();

      window.addEventListener("scroll", scrollHandler, { passive: true });
      window.addEventListener("resize", resizeHandler);

      // Pause work when tab hidden (desktop PDP case)
      const visHandler = () => {
        if (document.hidden) this._stopRaf();
        else if (this.isPDP && !this.isMobile && !this.prefersReducedMotion && this.heroEl) this._startRaf();
      };
      document.addEventListener("visibilitychange", visHandler);

      this.$cleanup = () => {
        resizeObserver.disconnect();
        this._nearViewportObserver?.disconnect();
        this._stopRaf();
        window.removeEventListener("scroll", scrollHandler);
        window.removeEventListener("resize", resizeHandler);
        document.removeEventListener("visibilitychange", visHandler);

        if (this._prmMedia) {
          if (this._prmMedia.removeEventListener) {
            this._prmMedia.removeEventListener("change", this._onPrmChange);
          } else if (this._prmMedia.removeListener) {
            this._prmMedia.removeListener(this._onPrmChange);
          }
        }
      };
    }
  };
}

export default header;
